import type { PayloadOf, UIEvents, UIEventType, UISignals, UISignalType } from './index';
import type { ReadonlySignal } from '@preact/signals-react';

/**
 * EventBus interface for type-safe signal access and event emission.
 *
 * Uses mapped types for end-to-end type safety:
 * - TSignals: A mapped type like { 'widget::instrument': ReadonlySignal<string>, ... }
 * - TEvents: A mapped type like { 'widget::instrumentChange': { instrument: string }, ... }
 *
 * @example
 * import type { InstrumentSignal, ChannelSignal, InstrumentChangeEvent } from 'src/types/EventBus';
 *
 * interface MyWidgetModel {
 *     eventBus: EventBus<
 *         InstrumentSignal & ChannelSignal,
 *         InstrumentChangeEvent
 *     >;
 * }
 */
export interface EventBus<
    TSignals extends Record<string, ReadonlySignal<unknown>>,
    TEvents extends Record<string, unknown>,
> {
    getSignal<K extends keyof TSignals>(key: K): TSignals[K];
    emit<K extends keyof TEvents>(key: K, payload: TEvents[K]): void;

    /** Returns true if this EventBus has a signal registered for the given key */
    hasSignal(key: PropertyKey): boolean;

    /** Returns true if this EventBus has an event handler registered for the given key */
    hasEvent(key: PropertyKey): boolean;

    /** Returns an iterable of all signal keys supported by this EventBus */
    get signalKeys(): Iterable<string>;

    /** Returns an iterable of all event keys supported by this EventBus */
    get eventKeys(): Iterable<string>;
}

export type EventBusSignals<TEventBus> = TEventBus extends EventBus<infer TSignals, infer _TEvents> ? TSignals : never;

export type EventBusEvents<TEventBus> = TEventBus extends EventBus<infer _TSignals, infer TEvents> ? TEvents : never;

// ----- builder type maps -----
type SignalFactoryMap = Record<string, () => ReadonlySignal<unknown>>;
type EventHandlerMap = Record<string, (payload: never) => void>;

// Type helpers for extracting signal types from factory maps
type ExtractSignals<T extends SignalFactoryMap> = { [K in keyof T]: ReturnType<T[K]> };
type ExtractEventPayloads<T extends EventHandlerMap> = { [K in keyof T]: Parameters<T[K]>[0] };

/**
 * Builder for constructing an EventBus with type-safe signal and event registration.
 *
 * Signal and event keys are constrained to UISignalType and UIEventType respectively,
 * ensuring that only valid keys from the global type definitions can be used.
 *
 * Call `.build()` to get the final EventBus instance.
 */
export class EventBusBuilder<TSignals extends SignalFactoryMap, TEvents extends EventHandlerMap> {
    readonly #signalFactories: TSignals;
    readonly #eventHandlers: TEvents;

    private constructor(signalFactories: TSignals, eventHandlers: TEvents) {
        this.#signalFactories = signalFactories;
        this.#eventHandlers = eventHandlers;
    }

    static create() {
        // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- marker interface
        return new EventBusBuilder({} as {}, {} as {});
    }

    addSignal<K extends UISignalType>(
        key: K,
        factory: () => UISignals[K],
    ): EventBusBuilder<TSignals & Record<K, () => UISignals[K]>, TEvents> {
        return new EventBusBuilder(
            {
                ...(this.#signalFactories as any),
                [key]: factory,
            } as any,
            this.#eventHandlers,
        );
    }

    addEvent<K extends UIEventType>(
        key: K,
        handler: (payload: PayloadOf<K>) => void,
    ): EventBusBuilder<TSignals, TEvents & Record<K, (payload: PayloadOf<K>) => void>> {
        return new EventBusBuilder(this.#signalFactories, {
            ...(this.#eventHandlers as any),
            [key]: handler,
        } as any);
    }

    // ----- composition -----
    // forbid overlapping keys at compile-time on both axes
    merge<
        USignals extends SignalFactoryMap & { [K in keyof TSignals]?: never },
        UEvents extends EventHandlerMap & { [K in keyof TEvents]?: never },
    >(other: EventBusBuilder<USignals, UEvents>): EventBusBuilder<TSignals & USignals, TEvents & UEvents> {
        return new EventBusBuilder(
            {
                ...(this.#signalFactories as any),
                ...((other as any).#signalFactories as any),
            } as any,
            {
                ...(this.#eventHandlers as any),
                ...((other as any).#eventHandlers as any),
            } as any,
        );
    }

    build(): EventBus<ExtractSignals<TSignals>, ExtractEventPayloads<TEvents>> {
        const signalFactories = this.#signalFactories;
        const eventHandlers = this.#eventHandlers;
        const signalCache = new Map<string, WeakRef<ReadonlySignal<unknown>>>();

        return {
            getSignal<K extends keyof ExtractSignals<TSignals>>(key: K): ExtractSignals<TSignals>[K] {
                const keyStr = key as string;
                const cached = signalCache.get(keyStr)?.deref();
                if (cached) {
                    return cached as ExtractSignals<TSignals>[K];
                }

                const signal = signalFactories[key as keyof TSignals]() as ExtractSignals<TSignals>[K];
                signalCache.set(keyStr, new WeakRef(signal));
                return signal;
            },

            emit<K extends keyof ExtractEventPayloads<TEvents>>(
                key: K,
                payload: ExtractEventPayloads<TEvents>[K],
            ): void {
                eventHandlers[key as keyof TEvents](payload as never);
            },

            hasSignal(key: PropertyKey): boolean {
                return key in signalFactories;
            },

            hasEvent(key: PropertyKey): boolean {
                return key in eventHandlers;
            },

            get signalKeys(): Iterable<string> {
                return Object.keys(signalFactories);
            },

            get eventKeys(): Iterable<string> {
                return Object.keys(eventHandlers);
            },
        };
    }
}

/**
 * A fluent wrapper that merges multiple EventBus instances with eager map building for O(1) lookups.
 *
 * @example
 * const merged = MergedEventBus.from(bus1).merge(bus2).merge(bus3);
 * const signal = merged.getSignal('widget::instrument');
 */
export class MergedEventBus<
    TSignals extends Record<string, ReadonlySignal<unknown>>,
    TEvents extends Record<string, unknown>,
> implements EventBus<TSignals, TEvents> {
    readonly #signalBusMap: Map<PropertyKey, EventBus<any, any>>;
    readonly #eventBusMap: Map<PropertyKey, EventBus<any, any>>;

    private constructor(
        signalBusMap: Map<PropertyKey, EventBus<any, any>>,
        eventBusMap: Map<PropertyKey, EventBus<any, any>>,
    ) {
        this.#signalBusMap = signalBusMap;
        this.#eventBusMap = eventBusMap;
    }

    static from<TBus extends EventBus<any, any>>(
        bus: TBus,
    ): MergedEventBus<EventBusSignals<TBus>, EventBusEvents<TBus>> {
        const signalBusMap = new Map<PropertyKey, EventBus<any, any>>();
        const eventBusMap = new Map<PropertyKey, EventBus<any, any>>();

        for (const key of bus.signalKeys) {
            signalBusMap.set(key, bus);
        }
        for (const key of bus.eventKeys) {
            eventBusMap.set(key, bus);
        }

        return new MergedEventBus(signalBusMap, eventBusMap) as any;
    }

    merge<UBus extends EventBus<any, any>>(
        other: UBus,
    ): MergedEventBus<TSignals & EventBusSignals<UBus>, TEvents & EventBusEvents<UBus>> {
        const signalBusMap = new Map(this.#signalBusMap);
        const eventBusMap = new Map(this.#eventBusMap);

        for (const key of other.signalKeys) {
            signalBusMap.set(key, other);
        }
        for (const key of other.eventKeys) {
            eventBusMap.set(key, other);
        }

        return new MergedEventBus(signalBusMap, eventBusMap) as any;
    }

    hasSignal(key: PropertyKey): boolean {
        return this.#signalBusMap.has(key);
    }

    hasEvent(key: PropertyKey): boolean {
        return this.#eventBusMap.has(key);
    }

    get signalKeys(): Iterable<string> {
        return this.#signalBusMap.keys() as Iterable<string>;
    }

    get eventKeys(): Iterable<string> {
        return this.#eventBusMap.keys() as Iterable<string>;
    }

    getSignal<K extends keyof TSignals>(key: K): TSignals[K] {
        const bus = this.#signalBusMap.get(key);
        if (!bus) {
            throw new Error(`Signal key not found: ${String(key)}`);
        }
        return bus.getSignal(key as any) as TSignals[K];
    }

    emit<K extends keyof TEvents>(key: K, payload: TEvents[K]): void {
        const bus = this.#eventBusMap.get(key);
        if (!bus) {
            throw new Error(`Event key not found: ${String(key)}`);
        }
        bus.emit(key as any, payload);
    }
}

/**
 * Convenience function to merge two EventBus instances.
 *
 * @example
 * const merged = mergeEventBus(bus1, bus2);
 */

export function mergeEventBus<TBus1 extends EventBus<any, any>, TBus2 extends EventBus<any, any>>(
    bus1: TBus1,
    bus2: TBus2,
): MergedEventBus<EventBusSignals<TBus1> & EventBusSignals<TBus2>, EventBusEvents<TBus1> & EventBusEvents<TBus2>> {
    return MergedEventBus.from(bus1).merge(bus2) as any;
}

/**
 * Type guard that checks if an EventBus supports the specified signals and events.
 * Narrows the type to include the checked keys from UISignals and UIEvents.
 *
 * @example
 * const bus: EventBus<any, any> = getEventBusFromSomewhere();
 * if (eventBusSupports(bus, ['widget::instrument'] as const, ['widget::instrumentChange'] as const)) {
 *     // bus is now EventBus<Pick<UISignals, 'widget::instrument'>, Pick<UIEvents, 'widget::instrumentChange'>>
 *     const signal = bus.getSignal('widget::instrument');
 * }
 */
export function eventBusSupports<SK extends UISignalType, EK extends UIEventType>(
    bus: EventBus<any, any>,
    signalKeys: readonly SK[],
    eventKeys: readonly EK[],
): bus is EventBus<Pick<UISignals, SK>, Pick<UIEvents, EK>> {
    return signalKeys.every((key) => bus.hasSignal(key)) && eventKeys.every((key) => bus.hasEvent(key));
}
